import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ExternalLink, Calendar, Building, Code } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import api, { getApiUrl } from '../utils/api';
import { createSanitizedMarkup } from '../utils/sanitize';

const API_URL = getApiUrl();

const ProjectDetail = () => {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await api.get(`/projects/${slug}`);
        setProject(response.data);
      } catch (error) {
        setError('Project not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-gray-400">{t.pages.loading}</div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center">
        <div className="text-gray-400 mb-4">Project not found</div>
        <Link to="/projeler" className="text-blue-400 hover:text-blue-300">
          {t.projects.allProjects}
        </Link>
      </div>
    );
  }

  const title = language === 'tr' ? project.title_tr : project.title_en;
  const shortDesc = language === 'tr' ? project.short_desc_tr : project.short_desc_en;
  const description = language === 'tr' ? project.detail_desc_tr : project.detail_desc_en;
  const imageUrl = project.image ? 
    (project.image.startsWith('http') ? project.image : `${API_URL}${project.image}`) : 
    null;

  // Parse technologies string into array
  const technologies = project.technologies ? project.technologies.split(',').map(t => t.trim()) : [];

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="project-detail-page">
      <div className="max-w-5xl mx-auto px-6">
        <ScrollReveal>
          {/* Back Link */}
          <Link 
            to="/projeler" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.projects.allProjects}
          </Link>

          {/* Header Image */}
          {imageUrl && (
            <div className="aspect-video rounded-2xl overflow-hidden mb-8 shadow-2xl shadow-blue-500/10">
              <img 
                src={imageUrl} 
                alt={title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Title & Meta */}
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{title}</h1>
            <p className="text-xl text-gray-400">{shortDesc}</p>
          </div>

          {/* Project Info Cards */}
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            {project.client && (
              <div className="p-4 rounded-xl glass">
                <div className="flex items-center gap-3 mb-2">
                  <Building className="w-5 h-5 text-blue-400" />
                  <span className="text-gray-400 text-sm">
                    {language === 'tr' ? 'Müşteri' : 'Client'}
                  </span>
                </div>
                <p className="font-semibold">{project.client}</p>
              </div>
            )}

            {project.year && (
              <div className="p-4 rounded-xl glass">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="w-5 h-5 text-purple-400" />
                  <span className="text-gray-400 text-sm">
                    {language === 'tr' ? 'Yıl' : 'Year'}
                  </span>
                </div>
                <p className="font-semibold">{project.year}</p>
              </div>
            )}

            {project.link && (
              <div className="p-4 rounded-xl glass">
                <div className="flex items-center gap-3 mb-2">
                  <ExternalLink className="w-5 h-5 text-cyan-400" />
                  <span className="text-gray-400 text-sm">
                    {language === 'tr' ? 'Proje Linki' : 'Project Link'}
                  </span>
                </div>
                <a 
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-blue-400 hover:text-blue-300 flex items-center gap-1"
                >
                  {language === 'tr' ? 'Siteyi Ziyaret Et' : 'Visit Site'}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}
          </div>

          {/* Technologies */}
          {technologies.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Code className="w-5 h-5 text-gray-400" />
                <h2 className="text-xl font-bold">
                  {language === 'tr' ? 'Kullanılan Teknolojiler' : 'Technologies Used'}
                </h2>
              </div>
              <div className="flex flex-wrap gap-3">
                {technologies.map((tech) => (
                  <span 
                    key={tech}
                    className="px-4 py-2 rounded-full glass text-sm font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">
              {language === 'tr' ? 'Proje Hakkında' : 'About the Project'}
            </h2>
            <div 
              className="prose prose-invert prose-lg max-w-none
                         prose-headings:text-white prose-headings:font-bold
                         prose-p:text-gray-300 prose-li:text-gray-300
                         prose-strong:text-white prose-a:text-blue-400
                         prose-img:rounded-xl"
              dangerouslySetInnerHTML={createSanitizedMarkup(description)}
            />
          </div>

          {/* Project Link Button */}
          {project.link && (
            <div className="mb-12">
              <motion.a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 
                         rounded-xl font-semibold group"
              >
                {language === 'tr' ? 'Projeyi Görüntüle' : 'View Project'}
                <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
            </div>
          )}

          {/* CTA */}
          <div className="p-8 rounded-2xl glass text-center">
            <h3 className="text-2xl font-bold mb-4">
              {language === 'tr' ? 'Benzer bir proje mi düşünüyorsunuz?' : 'Thinking of a similar project?'}
            </h3>
            <p className="text-gray-400 mb-6">{t.cta.subtitle}</p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="inline-block">
              <Link
                to="/iletisim"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold 
                         inline-flex items-center gap-2 group"
              >
                {t.cta.btn1}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
};

export default ProjectDetail;
